import {
  applySnapshot,
  flow,
  getSnapshot,
  SnapshotOrInstance,
  types,
} from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { getConvertedDurationHoursMins, reorder } from "utils/calculation";

import {
  ActiveProgram,
  NutritionCategoryModel,
  PROGRAM_PERMISSION_TYPES,
  PUBLISH_STATUSES,
  SimpleModel,
} from "constants/models";
import { REORDER_MOVE_TYPES } from "constants/reorder";

export const MacroNutrientsModel = types.model({
  carbs: types.maybe(types.number),
  fiber: types.maybe(types.number),
  fat: types.maybe(types.number),
  protein: types.maybe(types.number),
  sugar: types.maybe(types.number),
  saturatedFat: types.maybe(types.number),
  transFat: types.maybe(types.number),
  cholesterol: types.maybe(types.number),
  sodium: types.maybe(types.number),
  vitaminA: types.maybe(types.number),
  vitaminC: types.maybe(types.number),
  calcium: types.maybe(types.number),
  iron: types.maybe(types.number),
});

export const RecipeModel = types
  .model({
    id: types.number,
    title: types.string,
    imageUrl: types.maybe(types.string),
    order: types.maybe(types.number),
    day: types.maybe(types.number),
    duration: types.maybe(types.number),
    foodMealType: types.maybe(SimpleModel),
    macronutrients: MacroNutrientsModel,
    created: types.maybe(types.string),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get durationText() {
      return getConvertedDurationHoursMins(self.duration);
    },
  }));

export const RecipeDetailsModel = types
  .model({
    id: types.number,
    title: types.string,
    duration: types.maybe(types.number),
    imageUrl: types.maybe(types.string),
    activePrograms: types.array(ActiveProgram),
  })
  .views(self => ({
    get accessFormData() {
      return {
        activePrograms: self.activePrograms.map(({ id, name, permission }) => ({
          id,
          name,
          switchState: permission === PROGRAM_PERMISSION_TYPES.PREVIEW,
        })),
      };
    },
  }));

const NutritionCategoryDetailsStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/food/recipe/list`, params);
  }),
  itemModel: RecipeModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(RecipeDetailsModel),
    isMoreLoading: types.optional(types.boolean, false),
    isRecipeLoading: types.optional(types.boolean, false),
    isDetailsManaging: types.optional(types.boolean, false),
    isRecipeReordering: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    getReorderInfo(
      reorderedItems: SnapshotOrInstance<typeof NutritionCategoryModel>[],
      recipeId: number,
    ) {
      const recipeIdNewIndex = reorderedItems.findIndex(
        (item: SnapshotOrInstance<typeof NutritionCategoryModel>) =>
          item.id === recipeId,
      );

      switch (recipeIdNewIndex) {
        case 0:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.TOP_OF_PAGE,
          };
        case reorderedItems.length - 1:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.BOTTOM_OF_PAGE,
          };
        default:
          return {
            afterId: reorderedItems[recipeIdNewIndex - 1].id,
            moveType: REORDER_MOVE_TYPES.NORMAL,
          };
      }
    },
  }))
  .actions(self => ({
    fetchMoreRecipes: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/food/recipe/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
            search: self.search,
            ...self.filters,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),
  }))
  .actions(self => ({
    fetchAllRecipes: flow(function*() {
      while (self.hasMoreInfo) {
        yield self.fetchMoreRecipes();
      }
    }),

    fetchRecipeDetails: flow(function*(categoryId: number, recipeId: number) {
      self.isRecipeLoading = true;

      try {
        const details = yield api.get(
          `/food/category/${categoryId}/recipe/${recipeId}`,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isRecipeLoading = false;
      }
    }),

    addRecipeToCategory: flow(function*(payload: any) {
      self.isDetailsManaging = true;

      try {
        return yield api.post(
          `/food/category/${payload.categoryId}/add/recipe`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isDetailsManaging = false;
      }
    }),

    removeRecipeFromCategory: flow(function*(payload: any) {
      self.isDetailsManaging = true;

      try {
        return yield api.delete(
          `/food/category/${payload.categoryId}/remove/recipe`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isDetailsManaging = false;
      }
    }),

    updateRecipeAccessPrograms: flow(function*(payload: any) {
      self.isDetailsManaging = true;

      try {
        const { categoryId, recipeId, ...data } = payload;

        yield api.put(`/food/category/${categoryId}/recipe/${recipeId}`, data);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isDetailsManaging = false;
      }
    }),

    reorderRecipes: flow(function*({
      categoryId,
      startIndex,
      endIndex,
      dayOfWeek,
      foodMealTypeId,
    }: {
      categoryId: number;
      startIndex: number;
      endIndex: number;
      dayOfWeek?: number;
      foodMealTypeId?: number;
    }) {
      self.isRecipeReordering = true;
      const { items } = getSnapshot(self);

      try {
        if (dayOfWeek && foodMealTypeId) {
          const itemsToReorder = items.filter(
            ({ day, foodMealType }) =>
              day === dayOfWeek && foodMealTypeId === foodMealType.id,
          );

          const recipeId = itemsToReorder[startIndex].id;
          const reorderedItemsGroup = reorder(
            itemsToReorder,
            startIndex,
            endIndex,
          );
          const reorderInfo = self.getReorderInfo(
            reorderedItemsGroup,
            recipeId,
          );

          const startIndexToReplace = items.findIndex(
            ({ day, foodMealType }) =>
              day === dayOfWeek && foodMealTypeId === foodMealType.id,
          );

          const reorderedItems = [...items];
          reorderedItems.splice(
            startIndexToReplace,
            reorderedItemsGroup.length,
            ...reorderedItemsGroup,
          );

          applySnapshot(self, { ...self, items: reorderedItems });

          const payload = {
            id: recipeId,
            dayOfWeek,
            foodMealTypeId,
            ...reorderInfo,
          };
          yield api.post(`/food/category/${categoryId}/recipe/move`, payload);
        } else {
          const recipeId = items[startIndex].id;
          const reorderedItems = reorder(items, startIndex, endIndex);
          applySnapshot(self, { ...self, items: reorderedItems });

          const reorderInfo = self.getReorderInfo(reorderedItems, recipeId);

          yield api.post(`/food/category/${categoryId}/recipe/move`, {
            id: recipeId,
            ...reorderInfo,
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        applySnapshot(self, { ...self, items });
        throw error;
      } finally {
        self.isRecipeReordering = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default NutritionCategoryDetailsStore.create();
